import axios from 'axios'
import store from '@/store'
import router from '@/router'
import { notification } from 'ant-design-vue'

const pendingRequests = new Map()
let requestIdCounter = 0

export const abortAllPendingRequests = () => {
  pendingRequests.forEach((controller) => {
    try {
      controller.abort()
    } catch (err) {
    }
  })
  pendingRequests.clear()
}

const getBaseUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.host}/`;
  } catch (error) {
    console.error('Invalid URL:', url);
    return url;
  }
};

const client = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  /** @NOTE : Custom options */
  notifications: false,
  consoles: true,
})

const openClient = axios.create({
  baseURL: process.env.VUE_APP_OPEN_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  /** @NOTE : Custom options */
  notifications: false,
  consoles: true,
})

const initApiClientFastify = axios.create({
  baseURL: process.env.VUE_APP_API_URL_FASTIFY,
  headers: { 'Content-Type': 'application/json' },
})

const initApiClientAdm = axios.create({
  baseURL: getBaseUrl(process.env.VUE_APP_API_URL_FASTIFY),
  headers: { 'Content-Type': 'application/json' },
})



const validateAndSetHeader = (headers, key, value) => {
  if (value == null || value === '' || value === 'null') {
    return headers;
  }

  const normalizedKey = key.toLowerCase();
  const existingKey = Object.keys(headers).find(k => k.toLowerCase() === normalizedKey);

  if (existingKey) {
    if (!headers[existingKey] || headers[existingKey] === 'null') {
      headers[existingKey] = value;
    }
  } else {
    headers[key] = value;
  }

  return headers;
};

client.interceptors.request.use(
  request => {
    const controller = new AbortController()
    const requestId = ++requestIdCounter
    request.signal = controller.signal
    request._requestId = requestId
    pendingRequests.set(requestId, controller)

    if (store?.state?.user?.accessToken) {
      request.headers = validateAndSetHeader(request.headers, 'Authorization', `Bearer ${store.state.user.accessToken}`)
    }

    if (
      store.state.user.workspace_id_active
    ) {
      request.headers['Workspace-Id'] =
        store.state.user.workspace_id_active
    }

    const urlParams = new URLSearchParams(window.location.search);
    let business_id = urlParams.get('business_id');

    if (business_id && business_id !== 'null' && business_id.trim() !== '') {
      request.headers = validateAndSetHeader(request.headers, 'business-id', business_id.trim());
    }
    if (request.url?.includes('channel/distributor/')) {
      const channelId = router.currentRoute?.params?.id;
      if (channelId) {
        request.headers = validateAndSetHeader(request.headers, 'Channel-Id', channelId);
      }
    }

    return request
  },
  error => {
    return Promise.reject(error)
  },
)

openClient.interceptors.request.use(
  request => {
    if (store?.state?.user?.accessToken) {
      request.headers = validateAndSetHeader(request.headers, 'Authorization', `Bearer ${store.state.user.accessToken}`)
    }

    if (
      store.state.user.workspace_id_active
    ) {
      request.headers['Workspace-Id'] =
        store.state.user.workspace_id_active
    }

    const urlParams = new URLSearchParams(window.location.search);
    let business_id = urlParams.get('business_id');

    if (business_id && business_id !== 'null' && business_id.trim() !== '') {
      request.headers = validateAndSetHeader(request.headers, 'business-id', business_id.trim());
    }

    return request
  },
  error => {
    return Promise.reject(error)
  },
)

initApiClientFastify.interceptors.request.use(request => {
  if (store.state.user.accessToken) {
    request.headers.Authorization = `Bearer ${store.state.user.accessToken}`
  }

  return request
})

initApiClientAdm.interceptors.request.use(request => {
  if (store.state.user.accessToken) {
    request.headers.Authorization = `Bearer ${store.state.user.accessToken}`
  }

  return request
})

client.interceptors.response.use(
  response => {
    if (response.config._requestId) {
      pendingRequests.delete(response.config._requestId)
    }
    return response
  },
  error => {
    if (error.config?._requestId) {
      pendingRequests.delete(error.config._requestId)
    }

    const { config, response } = error || {}
    const { notifications, consoles, customError } = config || {}

    if (notifications) {
      const { data, statusText, status: state } = response || {}
      const { status, message, error: title } = data || {}
      notification.error({
        message: status || state || error.message,
        description: customError || (title || message ? title || message : message || statusText),
      })
    }

    if (consoles) {
      console.error(error)
    }
    return Promise.reject(error)
  },
)

openClient.interceptors.response.use(
  response => {
    return response
  },
  error => {
    const { config, response } = error || {}
    const { notifications, consoles, customError } = config || {}

    if (notifications) {
      const { data, statusText, status: state } = response || {}
      const { status, message, error: title } = data || {}
      notification.error({
        message: status || state || error.message,
        description: customError || (title || message ? title || message : message || statusText),
      })
    }

    if (consoles) {
      console.error(error)
    }
    return Promise.reject(error)
  },
)

export const apiClient = client
export const apiClientFastify = initApiClientFastify
export const apiClientAdm = initApiClientAdm
export const apiOpenClient = openClient
export default client
